// Pre-session mood check-in. Kid taps one of four faces (or skip) before the first
// exercise. The chosen mood is posted to POST /session/mood and threaded through
// Storyteller + Tuner tone hints for the rest of the session. Feature A1 of the
// 2026-04-21 overhaul plan.

import { useEffect, useRef, useState } from 'react';
import { setSessionMood } from '../api/client';
import type { SessionMood } from '../api/client';

interface Props {
  childName: string;
  onDone: (mood: SessionMood) => void;
}

interface Choice {
  mood: SessionMood;
  emoji: string;
  label: string;
}

const CHOICES: Choice[] = [
  { mood: 'happy', emoji: '😀', label: 'Toll!' },
  { mood: 'ok',    emoji: '🙂', label: 'Geht so.' },
  { mood: 'meh',   emoji: '😐', label: 'Na ja.' },
  { mood: 'sad',   emoji: '😢', label: 'Traurig.' },
];

export default function EmotionalCheckIn({ childName, onDone }: Props) {
  const [pending, setPending] = useState<SessionMood | null>(null);
  const firstButtonRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    const id = window.setTimeout(() => firstButtonRef.current?.focus(), 0);
    return () => window.clearTimeout(id);
  }, []);

  async function choose(mood: SessionMood) {
    if (pending) return;
    setPending(mood);
    try {
      await setSessionMood(mood);
    } catch (err) {
      console.error('[mood] persist failed', err);
    }
    onDone(mood);
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="mood-title"
      style={{
        position: 'fixed', inset: 0,
        background: 'rgba(30,24,18,0.55)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 9500, animation: 'fade-in-up 0.2s ease-out',
      }}
    >
      <div
        style={{
          background: 'var(--parchment)', padding: 32, borderRadius: 8,
          width: 'min(480px, 92vw)', boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
          fontFamily: 'var(--serif)',
        }}
      >
        <h2
          id="mood-title"
          style={{
            margin: 0,
            fontFamily: 'var(--display)', fontSize: 24, fontWeight: 600,
            textAlign: 'center',
          }}
        >
          {`Wie fühlst du dich heute${childName ? ', ' + childName : ''}?`}
        </h2>
        <p style={{
          margin: '10px 0 24px', fontSize: 14, fontStyle: 'italic',
          color: 'var(--ink-muted)', textAlign: 'center',
        }}>
          Finn möchte wissen, wie es dir geht — damit er gut auf dich achten kann.
        </p>

        <div
          role="radiogroup"
          aria-label="Stimmung"
          style={{
            display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)',
            gap: 12,
          }}
        >
          {CHOICES.map((c, idx) => {
            const active = pending === c.mood;
            return (
              <button
                key={c.mood}
                ref={idx === 0 ? firstButtonRef : undefined}
                role="radio"
                aria-checked={active}
                aria-label={`${c.emoji} ${c.label}`}
                disabled={pending !== null}
                onClick={() => choose(c.mood)}
                style={{
                  minHeight: 96, padding: '14px 10px',
                  background: active ? 'var(--amber)' : 'var(--parchment)',
                  border: active ? 'none' : '1.5px solid rgba(60,50,40,0.2)',
                  borderRadius: 6, cursor: pending ? 'default' : 'pointer',
                  fontFamily: 'var(--serif)',
                  display: 'flex', flexDirection: 'column',
                  alignItems: 'center', justifyContent: 'center', gap: 6,
                  opacity: pending && !active ? 0.45 : 1,
                  transition: 'background 0.15s, transform 0.1s',
                }}
              >
                <span aria-hidden="true" style={{ fontSize: 40, lineHeight: 1 }}>
                  {c.emoji}
                </span>
                <span style={{
                  fontSize: 14,
                  color: active ? '#fff' : 'var(--ink)',
                }}>{c.label}</span>
              </button>
            );
          })}
        </div>

        <div style={{ textAlign: 'center', marginTop: 20 }}>
          <button
            onClick={() => choose('skipped')}
            disabled={pending !== null}
            style={{
              minHeight: 44, padding: '8px 18px',
              background: 'transparent',
              border: 'none',
              fontFamily: 'var(--sans)', fontSize: 13,
              color: 'var(--ink-muted)', cursor: pending ? 'default' : 'pointer',
              textDecoration: 'underline',
            }}
          >Überspringen</button>
        </div>
      </div>
    </div>
  );
}
